import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { 
  UserRound, Heart, HelpCircle, Mail, 
  FileText, Brain, Users, Shield, 
  Sparkles, CheckCircle, ChevronDown,
  ClipboardCheck, BarChart3, Globe,
  Smartphone, Share, Plus, MoreVertical
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

// Pricing configuration with geo-based currency
const PRICING_CONFIG = {
  USD: { symbol: '$', basic: 9.99, professional: 24.99, clinical: 49.99, locale: 'en-US' },
  ZAR: { symbol: 'R', basic: 150, professional: 220, clinical: 600, locale: 'en-ZA' },
  GBP: { symbol: '£', basic: 7.99, professional: 19.99, clinical: 39.99, locale: 'en-GB' },
  EUR: { symbol: '€', basic: 8.99, professional: 22.99, clinical: 44.99, locale: 'de-DE' },
  AUD: { symbol: 'A$', basic: 14.99, professional: 37.99, clinical: 74.99, locale: 'en-AU' },
  NZD: { symbol: 'NZ$', basic: 15.99, professional: 39.99, clinical: 79.99, locale: 'en-NZ' },
  CAD: { symbol: 'C$', basic: 12.99, professional: 32.99, clinical: 64.99, locale: 'en-CA' },
};

// Country to currency mapping
const COUNTRY_CURRENCY = {
  ZA: 'ZAR', GB: 'GBP', DE: 'EUR', FR: 'EUR', IT: 'EUR', ES: 'EUR', NL: 'EUR',
  AU: 'AUD', NZ: 'NZD', CA: 'CAD', US: 'USD'
};

const MOCKUP_IMAGES = {
  dashboard: "https://static.prod-images.emergentagent.com/jobs/6f387e11-ff45-40bd-880f-f1cccd9432b9/images/bf3546d566af6340b0c27f8acade20d36a2f8a7987475a6cec69fdceb4a2d480.png",
  assessment: "https://static.prod-images.emergentagent.com/jobs/6f387e11-ff45-40bd-880f-f1cccd9432b9/images/7f857a74aeea9c6d5ad1eecf1ae5ccd386ffb2cabbacd3dd7c5c55c66e6d4255.png",
  report: "https://static.prod-images.emergentagent.com/jobs/6f387e11-ff45-40bd-880f-f1cccd9432b9/images/181beff7b2de91e3ecb549e04fdb521a6625c9274b4bf212e8d5de91e9767ab3.png"
};

export default function LoginPage({ onLogin }) {
  const [isLogin, setIsLogin] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetCode, setShowResetCode] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [helpForm, setHelpForm] = useState({ name: '', email: '', message: '' });
  const [currency, setCurrency] = useState('USD');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: 'therapist',
    data_processing_consent: false,
  });
  const [loading, setLoading] = useState(false);

  // Detect user's country and set appropriate currency
  useEffect(() => {
    const detectCurrency = async () => {
      try {
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const countryCode = data.country_code;
        if (COUNTRY_CURRENCY[countryCode]) {
          setCurrency(COUNTRY_CURRENCY[countryCode]);
        }
      } catch (error) {
        // Default to USD if detection fails
        setCurrency('USD');
      }
    };
    detectCurrency();
  }, []);

  const pricing = PRICING_CONFIG[currency];

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Guard: therapist signup requires explicit consent
    if (!isLogin && formData.role === 'therapist' && !formData.data_processing_consent) {
      toast.error('Please confirm the client-data-processing consent to register.');
      return;
    }
    setLoading(true);

    const postOnce = async () => {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      return axios.post(`${API}${endpoint}`, formData, { timeout: 15000 });
    };

    try {
      let response;
      try {
        response = await postOnce();
      } catch (err) {
        // Auto-retry ONCE on transient errors (network blip, cold start,
        // 502/503) so colleagues don't have to fail-and-retry manually.
        const status = err.response?.status;
        const transient = !status || status >= 500 || err.code === 'ECONNABORTED';
        if (transient) {
          await new Promise((r) => setTimeout(r, 800));
          response = await postOnce();
        } else {
          throw err;
        }
      }

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      onLogin(response.data.user);
      toast.success(isLogin ? 'Welcome back!' : 'Account created successfully!');
      window.location.href = '/';
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 429) {
        // Rate-limited: the user might be repeatedly mistyping, OR a
        // colleague on the same IP has been. Tell them clearly so they
        // stop hammering the button.
        toast.error(detail || 'Too many login attempts. Please wait a few minutes and try again.');
      } else if (status === 401) {
        toast.error('Email or password incorrect. Please check and try again.');
      } else if (!status || status >= 500) {
        toast.error("We couldn't reach PlayFirst just now. Please try again in a moment.");
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Check your email for a reset code!');
      setShowForgotPassword(false);
      setShowResetCode(true);
    } catch (error) {
      toast.success('If an account exists with that email, you will receive a reset code.');
      setShowForgotPassword(false);
      setShowResetCode(true);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        email: forgotEmail,
        code: resetCode.toUpperCase(),
        new_password: newPassword 
      });
      toast.success('Password reset successfully! Please log in.');
      setShowResetCode(false);
      setForgotEmail('');
      setResetCode('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired reset code');
    }
  };

  const handleHelpSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/support/contact`, helpForm);
      toast.success('Your message has been sent! We\'ll get back to you soon.');
      setShowHelp(false);
      setHelpForm({ name: '', email: '', message: '' });
    } catch (error) {
      toast.success('Your message has been sent! We\'ll get back to you soon.');
      setShowHelp(false);
    }
  };

  const features = [
    {
      icon: <ClipboardCheck className="w-6 h-6" />,
      title: "Comprehensive Assessments",
      description: "Track standardized tests, informal observations, and language samples all in one place."
    },
    {
      icon: <Brain className="w-6 h-6" />,
      title: "GLP-Informed Analysis",
      description: "Built-in support for Gestalt Language Processing stages and neurodiversity-affirming frameworks."
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "AI-Powered Reports",
      description: "Generate professional, strengths-based clinical reports with intelligent interpretation."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "Family Collaboration",
      description: "Parents can view progress, activities, and connect with their child's therapy journey."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Progress Tracking",
      description: "SOAP notes, goal tracking, and session documentation for every child."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Privacy-Focused",
      description: "HIPAA-aligned practices with secure data storage and therapist-controlled access."
    }
  ];

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-[#FDFBF7]/90 backdrop-blur-sm z-50 border-b border-stone-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="w-5 h-5 text-primary" />
            </div>
            <span className="text-xl font-serif text-primary-900">PlayFirst</span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => scrollToSection('features')} className="text-sm text-stone-600 hover:text-primary transition-colors">Features</button>
            <button onClick={() => scrollToSection('how-it-works')} className="text-sm text-stone-600 hover:text-primary transition-colors">How It Works</button>
            <button onClick={() => scrollToSection('pricing')} className="text-sm text-stone-600 hover:text-primary transition-colors">Pricing</button>
            <button onClick={() => scrollToSection('install')} className="text-sm text-stone-600 hover:text-primary transition-colors">Install App</button>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => { setIsLogin(true); setShowAuth(true); }}
              className="rounded-full"
              data-testid="nav-login-btn"
            >
              Sign in
            </Button>
            <Button 
              onClick={() => { setIsLogin(false); setShowAuth(true); }}
              className="rounded-full"
              data-testid="nav-signup-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-normal tracking-tight text-primary-900 leading-tight mb-6">
              Connection-first speech therapy
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-stone-600 mb-8">
              A neurodiversity-affirming platform that supports play-based, relationship-focused therapy. 
              Built for therapists and families who value connection over compliance.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => { setIsLogin(false); setShowAuth(true); }}
                className="rounded-full text-base px-8 shadow-lg shadow-primary/20 hover:shadow-xl transition-all"
                data-testid="hero-get-started-btn"
              >
                Start Free Trial
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => scrollToSection('how-it-works')}
                className="rounded-full text-base px-8"
              >
                See How It Works
              </Button>
            </div>
            <p className="mt-4 text-sm text-stone-500">No credit card required • 7-day free trial</p>
          </motion.div>

          {/* Hero Image */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-16 relative"
          >
            <div className="bg-gradient-to-b from-primary/5 to-transparent rounded-3xl p-4 md:p-8">
              <img 
                src={MOCKUP_IMAGES.dashboard} 
                alt="PlayFirst Dashboard"
                className="rounded-2xl shadow-2xl shadow-stone-200/50 w-full"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Everything you need for modern speech therapy
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Tools designed by clinicians, for clinicians. Streamline your workflow while maintaining the human connection.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 h-full bg-stone-50/50 border-stone-100 rounded-2xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary mb-4">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-medium text-primary-900 mb-2">{feature.title}</h3>
                  <p className="text-stone-600 text-sm leading-relaxed">{feature.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Simple, powerful workflow
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              From assessment to report in three easy steps
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full rounded-2xl bg-stone-50/50 border-stone-100">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium mb-4">
                  1
                </div>
                <h3 className="text-xl font-serif text-primary-900 mb-3">Enter assessment data</h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  Record standardized test scores, informal observations, language samples, and clinical impressions.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Standardized & non-standardized measures
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Language sample analysis
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    PROMPT hierarchy tracking
                  </li>
                </ul>
              </Card>
            </motion.div>

            {/* Step 2 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 h-full rounded-2xl bg-stone-50/50 border-stone-100">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium mb-4">
                  2
                </div>
                <h3 className="text-xl font-serif text-primary-900 mb-3">Generate AI interpretations</h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  Our AI understands neurodiversity-affirming language and GLP frameworks.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Profile-specific guidance (GLP, Analytical, AAC)
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Balanced strengths and challenges
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Functional impact explanations
                  </li>
                </ul>
              </Card>
            </motion.div>

            {/* Step 3 */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 h-full rounded-2xl bg-stone-50/50 border-stone-100">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium mb-4">
                  3
                </div>
                <h3 className="text-xl font-serif text-primary-900 mb-3">Build your report</h3>
                <p className="text-stone-600 text-sm leading-relaxed mb-4">
                  Review, edit, and finalize your report with professional formatting.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Copy tables to your documents
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Print professional PDFs
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Parent-friendly summaries
                  </li>
                </ul>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Simple, transparent pricing
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              Start with a free trial. No credit card required.
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-sm text-stone-500">
              <Globe className="w-4 h-4" />
              Prices shown in {currency}
            </div>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {/* Basic Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-6 h-full rounded-3xl border-stone-200 hover:border-primary/30 transition-colors">
                <div className="mb-4">
                  <h3 className="text-xl font-medium text-primary-900">Basic</h3>
                  <p className="text-sm text-stone-500 mt-1">For getting started</p>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-serif text-primary-900">{pricing.symbol}{pricing.basic}</span>
                  <span className="text-stone-500">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Up to 10 children
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    20 AI activities/month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Assessments & SOAP notes
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Email support
                  </li>
                </ul>
                <Button 
                  onClick={() => { setIsLogin(false); setShowAuth(true); }}
                  variant="outline"
                  className="w-full rounded-full"
                >
                  Start Free Trial
                </Button>
              </Card>
            </motion.div>

            {/* Professional Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <Card className="p-6 h-full rounded-3xl border-primary bg-primary/5 relative overflow-hidden">
                <div className="absolute top-3 right-3">
                  <span className="text-xs font-medium text-white bg-primary px-2 py-1 rounded-full">Popular</span>
                </div>
                <div className="mb-4">
                  <h3 className="text-xl font-medium text-primary-900">Pro</h3>
                  <p className="text-sm text-stone-500 mt-1">For independent therapists</p>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-serif text-primary-900">{pricing.symbol}{pricing.professional}</span>
                  <span className="text-stone-500">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Up to 20 children
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    50 AI activities/month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    10 AI reports/month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Parent collaboration portal
                  </li>
                </ul>
                <Button 
                  onClick={() => { setIsLogin(false); setShowAuth(true); }}
                  className="w-full rounded-full"
                >
                  Start Free Trial
                </Button>
              </Card>
            </motion.div>

            {/* Clinical Plan */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Card className="p-6 h-full rounded-3xl border-stone-200 hover:border-primary/30 transition-colors">
                <div className="mb-4">
                  <h3 className="text-xl font-medium text-primary-900">Clinical</h3>
                  <p className="text-sm text-stone-500 mt-1">For practices & teams</p>
                </div>
                <div className="mb-4">
                  <span className="text-3xl font-serif text-primary-900">{pricing.symbol}{pricing.clinical}</span>
                  <span className="text-stone-500">/month</span>
                </div>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Unlimited children
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    150 AI activities/month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    50 AI reports/month
                  </li>
                  <li className="flex items-center gap-2 text-sm text-stone-600">
                    <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                    Up to 5 team members
                  </li>
                </ul>
                <Button 
                  onClick={() => { setIsLogin(false); setShowAuth(true); }}
                  variant="outline"
                  className="w-full rounded-full"
                >
                  Start Free Trial
                </Button>
              </Card>
            </motion.div>
          </div>

          <p className="text-center text-sm text-stone-500 mt-8">
            Parents always free • All plans include 7-day free trial
          </p>
        </div>
      </section>

      {/* Add to Homescreen Section */}
      <section id="install" className="py-16 px-4 bg-gradient-to-b from-white to-blue-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-10"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-4">
              <Smartphone className="w-4 h-4" />
              Works Like an App
            </div>
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              No App Store Needed
            </h2>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              PlayFirst is a web app that works beautifully on any device. Add it to your homescreen for an app-like experience with one tap access.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* iOS Instructions */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-6 shadow-sm border border-stone-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-stone-900 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-primary-900">iPhone & iPad</h3>
                  <p className="text-sm text-stone-500">Safari browser</p>
                </div>
              </div>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">1</span>
                  <div className="flex-1">
                    <p className="text-stone-700">Open PlayFirst in <strong>Safari</strong></p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">2</span>
                  <div className="flex-1 flex items-center gap-2">
                    <p className="text-stone-700">Tap the</p>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-lg text-sm">
                      <Share className="w-4 h-4" /> Share
                    </span>
                    <p className="text-stone-700">button</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-sm font-medium">3</span>
                  <div className="flex-1 flex items-center gap-2">
                    <p className="text-stone-700">Scroll and tap</p>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-lg text-sm">
                      <Plus className="w-4 h-4" /> Add to Home Screen
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                  </span>
                  <p className="text-stone-700">Tap <strong>Add</strong> — PlayFirst is now on your homescreen!</p>
                </li>
              </ol>
            </motion.div>

            {/* Android Instructions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-3xl p-6 shadow-sm border border-stone-100"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-2xl bg-green-600 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                    <path d="M17.523 2.746a.776.776 0 0 0-.633.311l-1.074 1.56c-1.16-.634-2.548-.99-4.04-.99-1.493 0-2.882.356-4.041.99L6.66 3.057a.776.776 0 0 0-1.266.87l1.014 1.473C4.39 6.859 3.001 9.262 3.001 12c0 .19.007.378.02.565l-.003.01v7.65c0 .966.784 1.75 1.75 1.75h14.465c.966 0 1.75-.784 1.75-1.75v-7.65l-.003-.01c.013-.187.02-.375.02-.565 0-2.738-1.39-5.141-3.407-6.6l1.014-1.473a.776.776 0 0 0-.633-1.241l-.45.06zM8.25 10.5a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5zm7.5 0a1.25 1.25 0 1 1 0 2.5 1.25 1.25 0 0 1 0-2.5z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-primary-900">Android</h3>
                  <p className="text-sm text-stone-500">Chrome browser</p>
                </div>
              </div>
              <ol className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">1</span>
                  <div className="flex-1">
                    <p className="text-stone-700">Open PlayFirst in <strong>Chrome</strong></p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">2</span>
                  <div className="flex-1 flex items-center gap-2">
                    <p className="text-stone-700">Tap the</p>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-lg text-sm">
                      <MoreVertical className="w-4 h-4" /> Menu
                    </span>
                    <p className="text-stone-700">(3 dots)</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">3</span>
                  <div className="flex-1 flex items-center gap-2">
                    <p className="text-stone-700">Tap</p>
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-lg text-sm">
                      <Smartphone className="w-4 h-4" /> Add to Home screen
                    </span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-sm font-medium">
                    <CheckCircle className="w-4 h-4" />
                  </span>
                  <p className="text-stone-700">Tap <strong>Add</strong> — PlayFirst is now on your homescreen!</p>
                </li>
              </ol>
            </motion.div>
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mt-8 text-stone-500 text-sm"
          >
            Once added, open PlayFirst from your homescreen for instant, full-screen access — just like a native app.
          </motion.p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif text-primary-900 mb-4">
              Ready to transform your practice?
            </h2>
            <p className="text-lg text-stone-600 mb-8">
              Join therapists who are spending less time on paperwork and more time on connection.
            </p>
            <Button 
              size="lg"
              onClick={() => { setIsLogin(false); setShowAuth(true); }}
              className="rounded-full text-base px-8 shadow-lg shadow-primary/20 hover:shadow-xl transition-all"
            >
              Get Started Free
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-stone-100">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            <span className="text-lg font-serif text-primary-900">PlayFirst</span>
          </div>
          <div className="flex items-center gap-6 text-sm text-stone-500">
            <a href="/terms" className="hover:text-primary transition-colors">Terms of Service</a>
            <a href="/hipaa" className="hover:text-primary transition-colors">Privacy Policy</a>
            <button onClick={() => setShowHelp(true)} className="hover:text-primary transition-colors">Contact</button>
          </div>
          <p className="text-sm text-stone-400">© 2025 PlayFirst. All rights reserved.</p>
        </div>
      </footer>

      {/* Auth Dialog */}
      <Dialog open={showAuth} onOpenChange={setShowAuth}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="auth-dialog">
          <div className="space-y-6">
            <div className="text-center space-y-2">
              <div className="flex justify-center mb-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
              </div>
              <h3 className="text-2xl font-medium text-primary-700">
                {isLogin ? 'Welcome back' : 'Get started'}
              </h3>
              <p className="text-sm text-stone-500">
                {isLogin ? 'Sign in to your account' : 'Create your account'}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {!isLogin && (
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    data-testid="name-input"
                    type="text"
                    placeholder="Your name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="h-12 rounded-2xl"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="h-12 rounded-2xl"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  className="h-12 rounded-2xl"
                />
              </div>

              {!isLogin && (
                <div className="space-y-2">
                  <Label>Role</Label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      data-testid="role-therapist-btn"
                      onClick={() => setFormData({ ...formData, role: 'therapist' })}
                      className={`flex-1 h-12 rounded-2xl border-2 transition-all ${
                        formData.role === 'therapist'
                          ? 'border-primary bg-primary/5 text-primary font-medium'
                          : 'border-stone-200 text-stone-600 hover:border-primary/50'
                      }`}
                    >
                      Therapist
                    </button>
                    <button
                      type="button"
                      data-testid="role-parent-btn"
                      onClick={() => setFormData({ ...formData, role: 'parent' })}
                      className={`flex-1 h-12 rounded-2xl border-2 transition-all ${
                        formData.role === 'parent'
                          ? 'border-primary bg-primary/5 text-primary font-medium'
                          : 'border-stone-200 text-stone-600 hover:border-primary/50'
                      }`}
                    >
                      Parent
                    </button>
                  </div>
                </div>
              )}

              {!isLogin && formData.role === 'therapist' && (
                <div className="p-3 bg-stone-50 rounded-xl border border-stone-200">
                  <p className="text-xs text-stone-600 leading-relaxed">
                    <strong>Privacy Notice:</strong> PlayFirst stores limited therapy information entered by clinicians, 
                    including first name, age, and session notes. Therapists are responsible for obtaining parental consent 
                    and should avoid entering identifiable personal information.
                  </p>
                </div>
              )}

              {!isLogin && formData.role === 'therapist' && (
                <label
                  className={`flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                    formData.data_processing_consent
                      ? 'border-emerald-300 bg-emerald-50/50'
                      : 'border-amber-300 bg-amber-50/40'
                  }`}
                  data-testid="data-consent-label"
                >
                  <input
                    type="checkbox"
                    checked={!!formData.data_processing_consent}
                    onChange={(e) => setFormData({ ...formData, data_processing_consent: e.target.checked })}
                    className="mt-1 flex-shrink-0"
                    data-testid="data-consent-checkbox"
                    required
                  />
                  <span className="text-sm text-stone-800 leading-relaxed">
                    I confirm that I have obtained appropriate consent from clients, parents or legal guardians to capture and process client information using PlayFirst.
                  </span>
                </label>
              )}

              <Button
                type="submit"
                data-testid="submit-btn"
                disabled={loading || (!isLogin && formData.role === 'therapist' && !formData.data_processing_consent)}
                className="w-full h-12 rounded-full text-base font-medium shadow-lg shadow-primary/20 hover:shadow-xl transition-all"
              >
                {loading ? 'Please wait...' : isLogin ? 'Sign in' : 'Create account'}
              </Button>
            </form>

            <div className="text-center">
              <button
                type="button"
                data-testid="toggle-mode-btn"
                onClick={() => setIsLogin(!isLogin)}
                className="text-sm text-stone-600 hover:text-primary transition-colors"
              >
                {isLogin ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
              </button>
            </div>
            
            {isLogin && (
              <div className="text-center">
                <button
                  type="button"
                  data-testid="forgot-password-btn"
                  onClick={() => { setShowAuth(false); setShowForgotPassword(true); }}
                  className="text-sm text-stone-500 hover:text-primary transition-colors"
                >
                  Forgot your password?
                </button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Forgot Password Dialog */}
      <Dialog open={showForgotPassword} onOpenChange={setShowForgotPassword}>
        <DialogContent className="rounded-3xl" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Reset your password</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleForgotPassword} className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              Enter your email address and we&apos;ll send you a link to reset your password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="forgot-email">Email</Label>
              <Input
                id="forgot-email"
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value)}
                required
                className="h-12 rounded-2xl"
                data-testid="forgot-email-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid="send-reset-btn">
              <Mail className="w-4 h-4 mr-2" />
              Send reset code
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Code Dialog */}
      <Dialog open={showResetCode} onOpenChange={setShowResetCode}>
        <DialogContent className="rounded-3xl" data-testid="reset-code-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Enter reset code</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              Enter the 8-character code from your email and choose a new password.
            </p>
            <div className="space-y-2">
              <Label htmlFor="reset-code">Reset Code</Label>
              <Input
                id="reset-code"
                type="text"
                placeholder="XXXXXXXX"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value.toUpperCase())}
                required
                maxLength={8}
                className="h-12 rounded-2xl text-center text-xl tracking-widest font-mono"
                data-testid="reset-code-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                placeholder="••••••••"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="h-12 rounded-2xl"
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirm Password</Label>
              <Input
                id="confirm-password"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="h-12 rounded-2xl"
                data-testid="confirm-password-input"
              />
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid="reset-password-btn">
              Reset Password
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              className="w-full rounded-full"
              onClick={() => {
                setShowResetCode(false);
                setShowForgotPassword(true);
              }}
            >
              Didn&apos;t receive a code? Try again
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Help/Contact Dialog */}
      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="rounded-3xl" data-testid="help-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif">Contact Support</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleHelpSubmit} className="space-y-4 mt-4">
            <p className="text-sm text-stone-600">
              Having trouble? Send us a message and we&apos;ll help you out.
            </p>
            <div className="space-y-2">
              <Label htmlFor="help-name">Your name</Label>
              <Input
                id="help-name"
                type="text"
                placeholder="Your name"
                value={helpForm.name}
                onChange={(e) => setHelpForm({ ...helpForm, name: e.target.value })}
                required
                className="h-12 rounded-2xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="help-email">Email</Label>
              <Input
                id="help-email"
                type="email"
                placeholder="you@example.com"
                value={helpForm.email}
                onChange={(e) => setHelpForm({ ...helpForm, email: e.target.value })}
                required
                className="h-12 rounded-2xl"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="help-message">How can we help?</Label>
              <Textarea
                id="help-message"
                placeholder="Describe your issue or question..."
                value={helpForm.message}
                onChange={(e) => setHelpForm({ ...helpForm, message: e.target.value })}
                required
                className="rounded-2xl min-h-24"
              />
            </div>
            <Button type="submit" className="w-full rounded-full" data-testid="send-help-btn">
              Send message
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
